"use client";
/**
 * Real GoTrue-backed auth context for the platform admin.
 * Mirrors the shape of the source hirebee-landings useAuth hook.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
// Type-only import: erased at compile time, does NOT bundle supabase-js.
import type { User, Session } from "@supabase/supabase-js";

// PERF: supabase-js is loaded lazily. SiteHeader (mounted on EVERY marketing
// page) imports useAuth for its null-fallback, and a static supabaseClient
// import here dragged the whole supabase-js bundle (~64KB gz) into every
// marketing page. AuthProvider only mounts on /platform routes, so the dynamic
// import below only ever fires there.
const loadSupabase = async () => (await import("@/lib/supabaseClient")).getSupabaseClient();

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  // Stubs to satisfy platform components that check MFA fields
  mfaPending: boolean;
  mfaRequired: boolean;
  mfaVerified: boolean;
  verifyMFA: (code: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    loadSupabase().then((supabase) => {
      if (cancelled) return;

      // Hydrate from current session
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (_event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      );
      unsubscribe = () => subscription.unsubscribe();
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.signUp({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    const supabase = await loadSupabase();
    await supabase.auth.signOut();
  };

  const verifyMFA = async (_code: string) => ({ error: null });

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        mfaPending: false,
        mfaRequired: false,
        mfaVerified: false,
        verifyMFA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  // If called outside AuthProvider (e.g. in landing pages), return safe defaults.
  if (!ctx) {
    return {
      user: null as User | null,
      session: null as Session | null,
      loading: false,
      signIn: async () => ({ error: null }),
      signUp: async () => ({ error: null }),
      signOut: async () => {},
      mfaPending: false,
      mfaRequired: false,
      mfaVerified: false,
      verifyMFA: async () => ({ error: null }),
    };
  }
  return ctx;
}

export default useAuth;
